/* eslint-disable jsx-a11y/control-has-associated-label */
import { Button as ButtonMUI, Grid, Tooltip, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { toPattern } from 'vanilla-masker';
import { IBranchViewModel } from 'viewModels';
import { useTheme } from 'styled-components';
import { v4 as uuid } from 'uuid';
import { ReactComponent as EditPen } from '../../assets/icons/editPen.svg';
import { ReactComponent as DeleteIcon } from '../../assets/icons/IconTrashCan.svg';
import { Button, NavHeader } from '../../components';
import { ModalDeleteBranch } from '../../components/Modals/ModalDeleteBranch';
import { ModalEditBranch } from '../../components/Modals/ModalEditBranch';
import { ModalCreateBranch } from '../../components/Modals/ModalCreateBranch';
import { MenuOptionLeftCompany } from '../../components/MenuOptionLeft/Company';
import { MASK_CNPJ } from '../../constants';
import { useApp, useAuth } from '../../context';
import { Body, Container, Content } from './styles';
import { useBranch } from '../../context/branch';
import { useAlert } from '../../hooks/useAlert';
import TitlePage from '../../components/TitlePage';
import JoyrideTour from '../../components/JoyrideTour';
import { TutorialType } from '../../types/enums';
import { CompanyController } from '../../controller/CompanyController';

export function BranchOffice() {
  const { user } = useAuth();
  const alert = useAlert();
  const app = useApp();
  const { colors } = useTheme();
  const { branches, setBranches, isLoadingBranches } = useBranch();
  const [isOpenDeleteModal, setIsOpenDeleteModal] = useState(false);
  const [isOpenEditModal, setIsOpenEditModal] = useState(false);
  const [isOpenCreateModal, setIsOpenCreateModal] = useState(false);
  const [isRequesting, setIsRequesting] = useState(false);
  const [branchSelected, setBranchSelected] = useState<IBranchViewModel>();
  const branchToTutorial: IBranchViewModel[] = [
    {
      id: uuid(),
      cnpj: '00000000000000',
      companyId: user?.id,
      address: {
        latitude: 0,
        longitude: 0,
        uf: 'SP',
        ufCode: 0,
        city: 'São Paulo',
        cityCode: 0,
        street: 'Rua Tutorial',
        number: 123,
        postalCode: '00000000',
      },
      name: 'Filial Tutorial',
    },
  ];
  const [runningTutorial, setRunningTutorial] = useState(false);

  const steps = [
    {
      target: '#start-item',
      content:
        'As filiais são as novas unidades da sua empresa, que seguem as mesmas regras e diretrizes, a partir desse menu é possível adicionar novas filiais, editar ou visualizar as filiais já cadastradas.',
      disableBeacon: true,
    },
    {
      target: '#add-new-branch',
      content:
        'Para cadastrar uma nova filial basta preencher o campo de CNPJ e clicar em adicionar. E preencher os dados da filial.',
    },
    {
      target: '#delete-branch',
      content:
        'Para excluir uma filial basta clicar na lixeira e confirmar clicando no botão excluir.',
    },
    {
      target: '#edit-branch',
      content:
        'Para editar uma filial basta clicar no botão de edição e alterar os campos desejados e clicar em salvar.',
    },
  ];

  const handleCreateBranch = async (branch: IBranchViewModel) => {
    if (runningTutorial) {
      alert.show({
        message: 'Isso é apenas uma demonstração! A filial não será cadastrada.',
        type: 'success',
      });
      setIsOpenCreateModal(false);
      return;
    }

    if (isRequesting) return;

    const isExists = branches.some((b) => b.cnpj === branch.cnpj);
    if (isExists) {
      alert.show({
        message: `CNPJ ${toPattern(branch.cnpj, MASK_CNPJ)} já cadastrado.`,
        type: 'error',
      });
      return;
    }

    setIsRequesting(true);
    app.setLoading(true);

    const newBranch: IBranchViewModel = {
      ...branch,
      id: uuid(),
      companyId: user?.id || '',
    };
    
    await CompanyController.createBranch(newBranch)
      .then(() => {
        setBranches((state) => [...state, newBranch]);
        alert.show({ message: 'Filial cadastrada com sucesso!', type: 'success' });
        setIsOpenCreateModal(false);
      })
      .catch(() => {
        alert.show({ message: 'Erro ao cadastrar filial!', type: 'error' });
      })
      .finally(() => {
        setIsRequesting(false);
        app.setLoading(false);
      });
  };

  const handleRemoveBranch = () => {
    if (runningTutorial) {
      alert.show({
        message: 'Isso é apenas uma demonstração! A filial não será removida.',
        type: 'success',
      });
      return;
    }
    if (!branchSelected) return;
    setBranches((state) => state.filter((b) => b.cnpj !== branchSelected?.cnpj));
    setIsOpenDeleteModal(false);
  };

  const handleEditBranch = (branch: IBranchViewModel) => {
    if (runningTutorial) {
      alert.show({
        message: 'Isso é apenas uma demonstração! Os dados não serão alterados.',
        type: 'success',
      });
      return;
    }
    if (!branchSelected) return;
    setBranches((prev) => {
      let newBranches = [...prev];
      const branchIdx = newBranches.findIndex((b) => b.cnpj === branch.cnpj);
      newBranches = [
        ...newBranches.slice(0, branchIdx),
        branch,
        ...newBranches.slice(branchIdx + 1),
      ];
      return newBranches;
    });
    setBranchSelected(undefined);
    setIsOpenEditModal(false);
  };

  useEffect(() => {
    app.setLoading(isLoadingBranches);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoadingBranches]);

  useEffect(() => {
    if (!user?.tutorialProgress?.some((p) => p === TutorialType.BRANCH_OFFICE)) {
      setRunningTutorial(true);
    }
  }, [user.tutorialProgress]);
  return (
    <>
      <JoyrideTour
        steps={steps}
        type={TutorialType.BRANCH_OFFICE}
        run={runningTutorial}
        onCallbackFinished={() => setRunningTutorial(false)}
      />
      <NavHeader typeHeader="company" />
      <Body>
        <MenuOptionLeftCompany />
        <Container>
          <TitlePage
            id="start-item"
            title="Filiais"
            onRequestOpen={() => {
              setRunningTutorial(true);
            }}
          />
          <Content>
            <div className="contentInfo">
              <h3>Gerenciar Filiais</h3>
              <p>Adicione e gerencie as filiais da sua empresa</p>
            </div>
            <div className="add">
              <Button
                text="Adicionar Filial"
                id="add-new-branch"
                dimensions={{ width: '13rem', height: '2.25rem' }}
                onClick={() => setIsOpenCreateModal(true)}
              />
            </div>
          </Content>
          {runningTutorial && (
            <>
              <h2>Cadastrados</h2>
              <div className="branchesList">
                {branchToTutorial.map((branch) => (
                  <Tooltip
                    disableInteractive
                    title={branch.name}
                    placement="top-start"
                    arrow
                    key={branch.id}>
                    <div className="branch" key={branch.cnpj}>
                      <div className="headerBranch">
                        <div className="containerName">
                          <p className="name">{branch.name}</p>
                        </div>
                        <div className="containerActions">
                          <ButtonMUI
                            type="button"
                            className="edit"
                            id="edit-branch"
                            onClick={() => {
                              setBranchSelected(branch);
                              setIsOpenEditModal(true);
                            }}>
                            <EditPen />
                          </ButtonMUI>
                          <ButtonMUI
                            type="button"
                            id="delete-branch"
                            className="delete"
                            onClick={() => {
                              setBranchSelected(branch);
                              setIsOpenDeleteModal(true);
                            }}>
                            {' '}
                            <DeleteIcon />
                          </ButtonMUI>
                        </div>
                      </div>
                      <div className="address">
                        <span className="street">
                          End.{' '}
                          <span className="streetInfo">
                            {branch?.address?.street ? branch?.address.street : '-'},{' '}
                            {branch?.address?.number ? branch?.address.number : ''} -{' '}
                            {branch?.address?.city ? branch?.address?.city : ''} -{' '}
                            {branch?.address?.uf ? branch?.address?.uf : '-'}
                          </span>
                        </span>
                      </div>
                    </div>
                  </Tooltip>
                ))}
              </div>
            </>
          )}
          {!runningTutorial && branches.length ? (
            <>
              <h2>Cadastrados</h2>
              <div className="branchesList">
                {branches.map((branch) => (
                  <Tooltip
                    disableInteractive
                    title={branch.name}
                    placement="top-start"
                    arrow
                    key={branch.id}>
                    <div className="branch" key={branch.cnpj}>
                      <div className="headerBranch">
                        <div className="containerName">
                          <p className="name">{branch.name}</p>
                        </div>
                        <div className="containerActions">
                          <ButtonMUI
                            type="button"
                            className="edit"
                            onClick={() => {
                              setBranchSelected(branch);
                              setIsOpenEditModal(true);
                            }}>
                            <EditPen />
                          </ButtonMUI>
                          <ButtonMUI
                            type="button"
                            className="delete"
                            onClick={() => {
                              setBranchSelected(branch);
                              setIsOpenDeleteModal(true);
                            }}>
                            {' '}
                            <DeleteIcon />
                          </ButtonMUI>
                        </div>
                      </div>
                      <div className="address">
                        <span className="street">
                          End. <span className="streetInfo">{branch?.address?.street}</span>
                        </span>
                      </div>
                    </div>
                  </Tooltip>
                ))}
              </div>
            </>
          ) : (
            <Grid container padding="2rem" justifyContent="center">
              <Typography justifyContent="center" align="center" color={colors.primary}>
                Nenhuma filial encontrada...
              </Typography>
            </Grid>
          )}
        </Container>
        <ModalDeleteBranch
          isOpen={isOpenDeleteModal}
          onRequestClose={() => {
            setIsOpenDeleteModal(false);
          }}
          runningTutorial={runningTutorial}
          currentBranch={branchSelected ?? ({} as IBranchViewModel)}
          onRequestRemoveBranch={handleRemoveBranch}
        />
        <ModalCreateBranch
          isOpen={isOpenCreateModal}
          runningTutorial={runningTutorial}
          onRequestClose={() => {
            if (!isRequesting) {
              setIsOpenCreateModal(false);
            }
          }}
          onRequestCreateBranch={handleCreateBranch}
          companyId={user?.id}
          isRequesting={isRequesting}
        />
        <ModalEditBranch
          isOpen={branchSelected !== undefined && isOpenEditModal}
          runningTutorial={runningTutorial}
          onRequestClose={() => {
            setBranchSelected(undefined);
            setIsOpenEditModal(false);
          }}
          currentBranch={branchSelected ?? ({} as IBranchViewModel)}
          onRequestEditBranch={handleEditBranch}
        />
      </Body>
    </>
  );
}
